import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
} from 'firebase/firestore';
import { useAuth } from './FirebaseAuthContext';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const deletingNotifications = useRef(new Set()); // Track notifications being deleted
  const readingNotifications = useRef(new Set()); // Track notifications being marked as read

  // ✅ QUOTA OPTIMIZATION: Polling instead of real-time listener
  // Polls every 30 seconds instead of continuous onSnapshot
  useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    let intervalId;
    let isActive = true;

    const fetchNotifications = async () => {
      if (!isActive) return;

      try {
        const notificationsRef = collection(db, 'users', user.uid, 'notifications');
        const q = query(
          notificationsRef,
          orderBy('createdAt', 'desc'),
          limit(50) // Only fetch 50 most recent
        );

        const snapshot = await getDocs(q);

        if (!isActive) return;

        const notifs = [];
        const messageNotifications = new Map();
        let unread = 0;

        snapshot.forEach((doc) => {
          const data = doc.data();
          const notification = {
            id: doc.id,
            ...data,
          };

          // Only show unread notifications
          if (!data.read) {
            // For NEW_MESSAGE notifications, only keep the latest per conversation
            if (data.type === 'NEW_MESSAGE') {
              const conversationId = data.data?.conversationId;
              if (conversationId) {
                const existing = messageNotifications.get(conversationId);
                if (!existing) {
                  messageNotifications.set(conversationId, notification);
                }
              } else {
                notifs.push(notification);
              }
            } else {
              notifs.push(notification);
            }

            unread++;
          }
        });

        // Add only the latest message notifications
        messageNotifications.forEach((notif) => {
          notifs.push(notif);
        });

        // Re-sort after adding message notifications
        notifs.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime;
        });

        setNotifications(notifs);
        setUnreadCount(unread);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchNotifications();

    // Poll every 30 seconds
    intervalId = setInterval(fetchNotifications, 30000);

    return () => {
      isActive = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [user?.uid]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    if (!user?.uid) return;

    // Prevent duplicate marks of the same notification
    if (readingNotifications.current.has(notificationId)) {
      return;
    }

    // ✅ OPTIMISTIC UPDATE: Update local state immediately
    setNotifications(prev =>
      prev.map(notif =>
        notif.id === notificationId ? { ...notif, read: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    readingNotifications.current.add(notificationId);

    try {
      const notifRef = doc(
        db,
        'users',
        user.uid,
        'notifications',
        notificationId
      );

      await updateDoc(notifRef, {
        read: true,
      });

      // Console statement removed for production
    } catch (error) {
      if (error.code === 'not-found') {
        // Console statement removed for production
      } else {
        // Error marking notification as read (silent in production)
      }
    } finally {
      // Remove from reading set after completion
      readingNotifications.current.delete(notificationId);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    if (!user?.uid) return;

    // Prevent duplicate deletes of the same notification
    if (deletingNotifications.current.has(notificationId)) {
      // Console statement removed for production
      return;
    }

    // ✅ OPTIMISTIC UPDATE: Remove from local state immediately
    const deletedNotif = notifications.find(n => n.id === notificationId);
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
    if (deletedNotif && !deletedNotif.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    deletingNotifications.current.add(notificationId);

    try {
      await deleteDoc(
        doc(db, 'users', user.uid, 'notifications', notificationId)
      );

      // Console statement removed for production
    } catch (error) {
      if (error.code === 'not-found') {
        // Console statement removed for production
      } else {
        // Error deleting notification (silent in production)
      }
    } finally {
      // Remove from deleting set after completion
      deletingNotifications.current.delete(notificationId);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    if (!user?.uid) return;

    try {
      const batch = [];

      for (const notif of notifications) {
        if (!notif.read) {
          const notifRef = doc(
            db,
            'users',
            user.uid,
            'notifications',
            notif.id
          );

          batch.push(
            updateDoc(notifRef, { read: true }).catch((error) => {
              if (error.code !== 'not-found') {
                // Error updating notification (silent in production)
              }
              return Promise.resolve();
            })
          );
        }
      }

      await Promise.all(batch);
      // Console statement removed for production
    } catch (error) {
      // Error marking all notifications as read (silent in production)
    }
  };

  // Clear all notifications
  const clearAllNotifications = async () => {
    if (!user?.uid) return;

    try {
      // Fetch ALL notifications for the user (not just the local limited state)
      const notificationsRef = collection(db, 'users', user.uid, 'notifications');
      const snapshot = await getDocs(notificationsRef);
      const batch = [];

      // Attempting to clear notifications (silent in production)

      for (const docSnapshot of snapshot.docs) {
        // Deleting notification (silent in production)
        batch.push(
          deleteDoc(doc(db, 'users', user.uid, 'notifications', docSnapshot.id))
            .catch((error) => {
              if (error.code !== 'not-found') {
                // Error deleting notification (silent in production)
              }
              return Promise.resolve();
            })
        );
      }

      await Promise.all(batch);

      // ✅ FIXED: Clear local state immediately
      setNotifications([]);
      setUnreadCount(0);
      // Console statement removed for production
    } catch (error) {
      // Error clearing notifications (silent in production)
    }
  };

  const value = {
    notifications,
    loading,
    unreadCount,
    markAsRead,
    deleteNotification,
    markAllAsRead,
    clearAllNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);

  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider'
    );
  }

  return context;
};

